import { Box, Stack, Typography } from '@mui/material';
import { Trans, useTranslation } from 'next-i18next';

import InfoTooltip from '@/components/ui/common/InfoTooltip';
import { formatMoneyByCurrency } from '@/utils';
import { styled } from '@mui/material/styles';

import { SRewardItem } from '@/apis/rewards';
import { TInferSchemaType } from '@/constants/schemas';
import Image, { ImageProps, StaticImageData } from 'next/image';
import React, { PropsWithChildren } from 'react';
import claimedIcon from './assets/claimed-icon.png';
import dailyIcon from './assets/daily.png';
import depositIcon from './assets/deposit.png';
import missedIcon from './assets/missed-icon.png';
import monthlyIcon from './assets/monthly.png';
import referralIcon from './assets/referral.png';
import rakebackIcon from './assets/stack.png';
import totalIcon from './assets/total-icon.png';
import weeklyIcon from './assets/weekly.png';
import dynamic from 'next/dynamic';
import { useCountDown, useRequest } from 'ahooks';
import getTimeString from '@/utils/string/getFormattedCountdown';
import LocalizedLink from '@/components/ui/LocalizedLink';
import MoneyWithCurrencyIcon from '../MoneyWithCurrencyIcon';

const Wrapper = styled((props: any) => (
  <Stack direction={'row'} {...props} alignItems={'center'}></Stack>
))(({ theme }) => ({}));

export const RewardsImage = ({
  name,
  ...rest
}: {
  name: string;
} & Omit<ImageProps, 'src' | 'alt'>) => {
  let srcImage: StaticImageData;
  switch (name) {
    case 'TOTAL':
      srcImage = totalIcon;
      break;
    case 'CLAIMED':
      srcImage = claimedIcon;
      break;
    case 'MISSED':
      srcImage = missedIcon;
      break;
    case 'RAKEBACK':
      srcImage = rakebackIcon;
      break;
    case 'DAILY':
      srcImage = dailyIcon;
      break;
    case 'WEEKLY':
      srcImage = weeklyIcon;
      break;
    case 'MONTHLY':
      srcImage = monthlyIcon;
      break;
    case 'REFERRAL':
      srcImage = referralIcon;
      break;
    case 'DEPOSITBONUS':
      srcImage = depositIcon;
      break;
    default:
      srcImage = rakebackIcon;
      break;
  }
  return <Image src={srcImage} alt={name} {...rest}></Image>;
};

const RewardsHeader: React.FC<{
  rewardType: TInferSchemaType<typeof SRewardItem>['bonus'];
  claimableAmount?: number;
  slot?: React.ReactNode;
  layout?: 'card' | 'list';
}> = ({ rewardType, claimableAmount, slot, layout = 'list' }) => {
  const [t] = useTranslation();

  let content;

  switch (rewardType) {
    case 'MONTHLY':
      content = (
        <>
          {t('Monthly Bonus')}
          <InfoTooltip
            text={
              <Trans
                i18nKey="It is a rakeback once every month. <br />Monthly Bonus = Total Wager of the last month x 1% x 3%.<br /><br />15th day of every month at 00:00 UTC."
                components={{
                  br: <br />,
                }}
              />
            }
          />
        </>
      );
      break;
    case 'RAKEBACK':
      content = (
        <>
          {t('Rakeback')}
          <InfoTooltip
            text={
              <Trans
                i18nKey="Take 6% of the house edge back from every bet you place. Rakeback is computed at 00:00 UTC every day."
                components={{
                  br: <br />,
                }}
              />
            }
          />
        </>
      );
      break;
    case 'DAILY':
      content = (
        <>
          {t('Daily Bonus')}
          <InfoTooltip
            text={t(
              'Daily Bonus is generated from Rakeback, Weekly Bonus, and Monthly Bonus, where a percentage of the amount is split into several days for you to claim. These daily bonuses will be summed up if more than one split amount occurs on the same day.',
            )}
          />
        </>
      );
      break;
    case 'WEEKLY':
      content = (
        <>
          {t('Weekly Bonus')}
          <InfoTooltip
            text={
              <Trans
                i18nKey="The Weekly Bonus is a rakeback once every week.<br />Weekly Bonus = Total Wager of last 7 Days x 1% x 5%.<br /><br />Weekly bonus is computed every Friday at 00:00 UTC."
                components={{
                  br: <br />,
                }}
              />
            }
          />
        </>
      );
      break;
    case 'REFERRAL':
      content = (
        <Stack>
          <Box display={'flex'} alignItems={'center'}>
            {t('Referral')}
            <InfoTooltip
              text={
                <Trans
                  i18nKey="You can earn from up to 3 tiers of referrals.<br /> · Earn 15% from your 1st tier referrals.<br /> · Earn 6% from your 2nd tier referrals (1st Tier’s referral)<br /> · Earn 4% from your 3rd tier referrals (2nd Tier’s referral)<br /><br />
Commissions will be calculated every day at 0:00 UTC."
                  components={{
                    br: <br />,
                  }}
                />
              }
            />
          </Box>
          <LocalizedLink href="/benefits#earn-lifetime-commission">
            <Typography color="rgb(253, 203, 53)" fontSize={'0.75rem'}>
              {t('Find Out More')}
            </Typography>
          </LocalizedLink>
        </Stack>
      );
      break;
    case 'DEPOSITBONUS':
      content = (
        <>
          {t('Deposit Bonus')}
          {/* <InfoTooltip
            text={
              <Trans
                i18nKey="Welcome Bonus Explain"
                components={{
                  br: <br />,
                }}
              />
            }
          /> */}
        </>
      );
      break;
    default:
      content = t('Reward');
  }

  if (layout === 'card') {
    return (
      <Stack gap={1} justifyContent={'flex-start'} flex={1}>
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'flex-start'}
        >
          <Typography
            fontSize={16}
            color={'text.secondary'}
            display={'flex'}
            alignItems={'center'}
            mb={2}
          >
            {content}
          </Typography>
        </Stack>
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'center'}
          position={'relative'}
          flex={1}
          maxHeight={rewardType === 'RAKEBACK' ? '93px' : '132px'}
          minHeight={rewardType === 'RAKEBACK' ? '93px' : '132px'}
        >
          <RewardsImage
            fill
            name={rewardType}
            style={{
              paddingLeft: rewardType === 'RAKEBACK' ? '30%' : '29%',
              paddingRight: rewardType === 'RAKEBACK' ? '30%' : '29%',
            }}
          />
        </Stack>

        {rewardType === 'RAKEBACK' ? (
          <Typography
            fontSize={18}
            textAlign={'center'}
            sx={{
              color: (theme) => theme.palette.green[500],
            }}
            mb={1}
            variant="caption"
          >
            {formatMoneyByCurrency(claimableAmount, {
              withCurrencySymbol: true,
            })}
          </Typography>
        ) : (
          <>
            <Typography color={'text.secondary'}>
              {rewardType === 'MONTHLY'
                ? t('rewards::Receive every 15th of the  month')
                : t('rewards::Receive every Friday')}
            </Typography>
            <Typography
              color={'text.secondary'}
              display={'flex'}
              justifyContent={'space-between'}
            >
              <span>{t('rewards::Min. wager required')}</span>
              <span>
                {formatMoneyByCurrency(rewardType === 'MONTHLY' ? 8000 : 1000, {
                  withCurrencySymbol: true,
                })}
              </span>
            </Typography>
            <Typography
              color={'text.secondary'}
              display={'flex'}
              justifyContent={'space-between'}
            >
              <span>
                {rewardType === 'MONTHLY'
                  ? t('rewards::The last monthly bonus')
                  : t('rewards::The last weekly bonus')}
              </span>
              <span>
                {claimableAmount
                  ? formatMoneyByCurrency(claimableAmount, {
                      withCurrencySymbol: true,
                    })
                  : '-'}
              </span>
            </Typography>
          </>
        )}
      </Stack>
    );
  }

  return (
    <Wrapper gap={2}>
      <RewardsImage width={40} height={40} name={rewardType} />

      <Stack>
        <Stack>
          <Stack direction={'row'}>{content}</Stack>
          {slot}
          {claimableAmount ? (
            <Typography
              sx={{
                color: (theme) => theme.palette.green[500],
              }}
              variant="caption"
            >
              <MoneyWithCurrencyIcon value={claimableAmount} />
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Wrapper>
  );
};

export const TickCircleWrapper = styled(Stack)(({ theme }) => ({
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  background: theme.palette.success.main,
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid transparent',
  cursor: 'pointer',
}));

export default RewardsHeader;

export const ExpireCountdown = ({
  expireTime,
  textOnly,
}: {
  expireTime: number | string;
  textOnly?: boolean;
}) => {
  const [, { days, hours, minutes, seconds }] = useCountDown({
    targetDate: Number(expireTime),
  });

  const countdowntext = `${days ? `${days}d ` : ''}${getTimeString({
    hours,
    minutes,
    seconds,
  })}`;

  if (textOnly) {
    return <>{countdowntext}</>;
  }

  return (
    <Typography noWrap color="text.help">
      <Trans
        i18nKey={'Next avail. in <countdown>{{countdown}}</countdown>'}
        components={{
          countdown: <Box component={'span'} color={'success.main'} />,
        }}
        values={{
          countdown: countdowntext,
        }}
      ></Trans>
    </Typography>
  );
};
